import {type AssetSourceComponentProps} from '@sanity/types'
import {PortalProvider} from '@sanity/ui'
import {type ForwardedRef, forwardRef, memo, useCallback, useEffect, useMemo, useState} from 'react'
import {encodeJsonParams} from 'sanity/router'

import {useClient} from '../../../../hooks'
import {useTranslation} from '../../../../i18n'
import {useWorkspace} from '../../../../studio'
import {DEFAULT_API_VERSION} from '../constants'
import {MediaLibraryProvider} from './MediaLibraryProvider'
import {OpenInSourceDialog} from './OpenInSourceDialog'
import {SelectAssetsDialog} from './SelectAssetsDialog'
import {UploadAssetsDialog} from './UploadAssetDialog'

const MediaLibraryAssetSourceComponent = function MediaLibraryAssetSourceComponent(
  props: AssetSourceComponentProps & {libraryId: string | null},
  ref: ForwardedRef<HTMLDivElement>,
) {
  const {
    accept, // TODO: make the plugin respect this filter?
    action = 'select',
    assetSource,
    assetToOpen,
    assetType = 'image',
    dialogHeaderTitle,
    libraryId: libraryIdProp,
    onChangeAction,
    onClose,
    onSelect,
    selectedAssets, // TODO: allow for pre-selected assets?
    schemaType,
    uploader,
  } = props

  const {t} = useTranslation()
  const client = useClient({apiVersion: DEFAULT_API_VERSION})
  const projectId = client.config().projectId
  const workspace = useWorkspace()
  const pickerPersistenceKey = useMemo(
    () =>
      encodeJsonParams({
        projectId: workspace.projectId,
        dataset: workspace.dataset,
        workspaceName: workspace.name,
      }) || undefined,
    [workspace.projectId, workspace.dataset, workspace.name],
  )
  const portalElement = useRootPortalElement()
  const handleSelectNewAsset = useCallback(() => {
    if (onChangeAction) {
      onChangeAction('select')
    }
  }, [onChangeAction])

  if (!projectId) {
    throw new Error('No projectId found')
  }

  const selectAssetType = assetType === 'sanity.video' ? 'video' : assetType

  return (
    <MediaLibraryProvider projectId={projectId} libraryId={libraryIdProp}>
      <UploadAssetsDialog
        open={action === 'upload'}
        onClose={onClose}
        onSelect={onSelect}
        schemaType={schemaType}
        uploader={uploader}
      />
      <PortalProvider element={portalElement}>
        <SelectAssetsDialog
          pickerPersistenceKey={pickerPersistenceKey}
          dialogHeaderTitle={
            dialogHeaderTitle ||
            t('asset-sources.media-library.select-dialog.title', {
              context: selectAssetType,
              targetTitle: schemaType?.title,
            })
          }
          open={action === 'select'}
          ref={ref}
          onClose={onClose}
          onSelect={onSelect}
          selection={[]}
          schemaType={schemaType}
          selectAssetType={selectAssetType}
        />
        {action === 'openInSource' && assetToOpen && (
          <OpenInSourceDialog
            pickerPersistenceKey={pickerPersistenceKey}
            asset={assetToOpen}
            dialogHeaderTitle={t('asset-sources.media-library.open-in-source-dialog.title')}
            selectNewAssetButtonLabel={
              schemaType?.title
                ? t('asset-sources.media-library.open-in-source-dialog.button.select-new-asset', {
                    targetTitle: schemaType.title,
                  })
                : t(
                    'asset-sources.media-library.open-in-source-dialog.button.select-new-asset-fallback',
                  )
            }
            onClose={onClose}
            onSelectNewAsset={handleSelectNewAsset}
          />
        )}
      </PortalProvider>
    </MediaLibraryProvider>
  )
}

export const MediaLibraryAssetSource = memo(forwardRef(MediaLibraryAssetSourceComponent))

const useRootPortalElement = () => {
  const [container] = useState(() => document.createElement('div'))

  useEffect(() => {
    container.classList.add('media-library-portal')
    document.body.appendChild(container)
    return () => {
      document.body.removeChild(container)
    }
  }, [container])

  return container
}
